import { useState, useEffect, useCallback } from 'react'
import { Table } from 'react-bootstrap'
import dayjs from '../../../utils/dayjsConfig'
import Pagination from '../../../layout/Pagination'
import ReactFilter from '../../../components/inputs/ReactFilterList'
import ReactMultipleFilter from '../../../components/inputs/ReactMultipleFilter'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faMagnifyingGlass,
  faQuestionCircle,
  faMapMarkedAlt
} from '@fortawesome/free-solid-svg-icons'
import { Button, Modal } from 'react-bootstrap'
import { clearCoordinates, formatMoney, formatActivityName } from '../../../helpers/utils'
import { MapModalProvider } from '../../../report/hook/MapModalProvider'
import ShowMapModalContent from '../../../report/map/ShowMapModalContent'
import { TASK_TYPES_KEYS } from '../../../pos/constants/constants'
import { ACTIVITIES_OPTIONS, BOOLEAN_OPTIONS } from '../../../report/unified/helpers/userReportTabs'
import LoadingTable from '../../../components/LoadingTable'
import GoogleIcon from '../../../components/icon/GoogleIcon'
import {
  ACTIVITY_STATUS_OPTIONS,
  ACTIVITY_STATUS_LABEL_TO_KEY
} from '../constants/ClientDashboardConst'

export default function ClientActivityDetails({
  tableData,
  usersFilterData,
  currentPage,
  setPagination,
  totalPages,
  handlePaginationClick,
  searchQuery,
  onSearchQuery,
  filterQuery,
  setFilterQuery,
  isTableDataFetching,
  isFilterDataFetching
}) {
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [resetReactFilters, setResetReactFilters] = useState(false)
  const [filters, setFilters] = useState([])
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())

  const [searchInput, setSearchInput] = useState('')

  const [show, setShow] = useState(false)
  const [dataSelected, setDataSelected] = useState(false)
  const [latitude, setLatitude] = useState(false)
  const [longitude, setLongitude] = useState(false)

  const activityStatusDic = [
    {
      key: 'Pedido',
      value: [
        'ENVIADO',
        'RECIBIDO',
        'DESPACHADO',
        'FACTURADO',
        'CANCELADO',
        'POR FACTURAR',
        'EN CAMINO',
        'PARCIAL',
        'ANULADO'
      ]
    },
    { key: 'Devolución', value: ['ENVIADO', 'RECIBIDO'] },
    { key: 'Entrega', value: ['POR ENTREGAR', 'ENTREGADO', 'RECHAZADO', 'PARCIAL'] },
    { key: 'Cobro', value: ['RECIBIDO', 'CANCELADO', 'ENVIADO'] },
    { key: 'Visita no efectiva', value: ['ENVIADO'] },
    {
      key: 'Ejecuciones de PDV',
      value: ['Inventario', 'Planimetria', 'Encuesta', 'Captura de Precios']
    }
  ]

  const filtersDisabled = isFilterDataFetching || !usersFilterData || usersFilterData.length === 0

  const headers = [
    'FECHA Y HORA',
    'USUARIO',
    'ACTIVIDAD',
    'CÓDIGO DE ACTIVIDAD',
    'MONTO',
    'DENTRO DE GEOCERCA',
    'UBICACIÓN',
    'ESTADO'
  ]

  useEffect(() => {
    prepareFilterData()
  }, [usersFilterData])

  const prepareFilterData = () => {
    const localLabelToIdMapping = {}
    if (usersFilterData == null) {
      return
    }
    const usersOptions = usersFilterData?.map((user) => {
      const displayText = user.label
      localLabelToIdMapping[displayText] = user.value
      return displayText
    })

    const visitedOptions = BOOLEAN_OPTIONS.map((option) => {
      localLabelToIdMapping[option.name] = option.value
      return option.name
    })

    //For activities
    ACTIVITIES_OPTIONS.map((option) => {
      localLabelToIdMapping[option.name] = option.value
    })

    const newFilters = [
      { key: 'NOMBRE DE USUARIO', value: usersOptions },
      { key: 'DENTRO DE GEOCERCA', value: visitedOptions }
    ]
    setFilters(newFilters)
    setLabelToIdMapping(localLabelToIdMapping)
  }

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      const labels = Array.isArray(selectedLabels)
        ? selectedLabels
        : Array.from(selectedLabels || [])
      const selectedIds = labels.map((label) => labelToIdMapping[label])
      updatedOptions.set(identifier, selectedIds)
      return updatedOptions
    })
  }

  const filterMappings = {
    'NOMBRE DE USUARIO': 'user_uid',
    'DENTRO DE GEOCERCA': 'visited',
    Pedido: 'PEDIDO',
    Devolución: 'DEVOLUCIÓN',
    Entrega: 'ENTREGA',
    Cobro: 'COBRO',
    'Visita no efectiva': 'VISITA NO EFECTIVA',
    INVENTARIO: 'inventory',
    PLANIMETRÍA: 'planimetry',
    ENCUESTA: 'survey',
    'CAPTURA DE PRECIOS': 'price_capture'
  }

  const pdvExecutionLabelToActivity = {
    Inventario: 'inventory',
    Planimetria: 'planimetry',
    Encuesta: 'survey',
    'Captura de Precios': 'price_capture'
  }

  const nonActivityFilterKeys = ['NOMBRE DE USUARIO', 'DENTRO DE GEOCERCA']
  const PDV_EXECUTION_KEY = 'Ejecuciones de PDV'

  const buildFiltersQuery = useCallback(() => {
    let queryParam = ''
    allSelectedOptions.forEach((value, key) => {
      if (key === PDV_EXECUTION_KEY) {
        if (!value || value.length === 0) {
          queryParam += `&activity=inventory,planimetry,survey,price_capture`
        } else {
          const activityParams = value
            .map((label) => pdvExecutionLabelToActivity[label])
            .filter(Boolean)
          if (activityParams.length > 0) {
            queryParam += `&activity=${activityParams.join(',')}`
          }
        }
        return
      }

      const paramKey = filterMappings[key]
      if (!paramKey) return

      if (nonActivityFilterKeys.includes(key)) {
        if (value.length !== 0) {
          const selectedValues = [...value]
          queryParam += `&${paramKey}=${selectedValues.join(',')}`
        }
      } else {
        queryParam += `&activity=${paramKey}`
        if (value.length > 0) {
          const statusKeys = value
            .map((label) => ACTIVITY_STATUS_LABEL_TO_KEY[label])
            .filter(Boolean)
          if (statusKeys.length > 0) {
            queryParam += `&status=${statusKeys.join(',')}`
          }
        }
      }
    })
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    setFilterQuery(queryParam)
  }, [allSelectedOptions, setPagination, setFilterQuery])

  useEffect(() => {
    buildFiltersQuery()
  }, [buildFiltersQuery])

  const handleSearch = (e) => {
    setSearchInput(e.target.value)
  }

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') {
      buildSearchQuery()
    }
  }

  const buildSearchQuery = () => {
    const searchText = searchInput ? searchInput.trim() : ''
    onSearchQuery(searchText)
  }

  const resetFilters = () => {
    setAllSelectedOptions(new Map())
    setSearchInput('')
    onSearchQuery('')
    setFilterQuery('')
    setResetReactFilters(!resetReactFilters)
  }

  const handleClose = () => setShow(false)

  const handleShow = (model, event) => {
    event.preventDefault()
    setDataSelected(model)
    setLatitude(clearCoordinates(model.latitude))
    setLongitude(clearCoordinates(model.longitude))
    setShow(true)
  }

  const handleTransactionClick = (event, activity) => {
    event.preventDefault()
    const activityName = activity.activity_name?.toLowerCase()
    const activityId = activity.activity_uid
    let url = ''
    switch (true) {
      case activityName.includes('pedido'):
        url = `/pedidos/${activityId}`
        break
      case activityName.includes('devoluci'):
      case activityName.includes('rechazo'):
        url = `/devoluciones/${activityId}`
        break
      case activityName.includes('no efectiva'):
        url = `/visitas-no-efectivas/${activityId}`
        break
      case activityName.includes('entrega'):
        url = `/entregas/${activityId}`
        break
      case activityName.includes('cobro'):
        url = `/cobros/${activityId}`
        break
      case TASK_TYPES_KEYS.includes(activityName):
        url = `/tareas_en_sitio/${activityId}`
        break
      case activityName.includes('liquida'):
        url = `/liquidaciones/detalles/${activityId}`
        break
    }

    if (url) window.open(url, '_blank')
  }

  const handleStatus = (activityStatus) => {
    const statusKey = activityStatus === 'send' ? 'sent' : activityStatus
    const option = ACTIVITY_STATUS_OPTIONS[statusKey] ?? ACTIVITY_STATUS_OPTIONS.received
    return <span className={option.className}>{option.label}</span>
  }

  return (
    <>
      <h4 className="text-sub-title mt-35">Filtrar actividades por:</h4>

      <div className="filters-search-container mb-10">
        <GoogleIcon className="filter-icon-poc" name="page_info" size={22} color="#4B6EA9" />
        <div className="filters-container">
          {filters.map((filter, indx) => (
            <div key={`flr-${indx}`} className="mr-10">
              <ReactFilter
                className="mr-10"
                title={filter.key}
                identifier={filter.key}
                options={filter.value}
                resetWatcher={resetReactFilters}
                onSelectionChange={updateAllSelectedOptions}
                disabled={filtersDisabled}
              />
            </div>
          ))}

          {/* Multiple Filter for Status */}
          <div key={`mul-flr-status`} className="mr-10">
            <ReactMultipleFilter
              className="mr-10"
              title={'ACTIVIDAD'}
              optionsKeys={[
                'Pedido',
                'Devolución',
                'Entrega',
                'Cobro',
                'Visita no efectiva',
                'Ejecuciones de PDV'
              ]}
              optionsDic={activityStatusDic}
              resetWatcher={resetReactFilters}
              allSelectedOptions={allSelectedOptions}
              disabled={filtersDisabled}
              sentRadio={true}
              onChange={(newAllSelectedOptions) => {
                setAllSelectedOptions(newAllSelectedOptions)
              }}
            />
          </div>
        </div>

        {/* Search */}
        <div className="search-input-containter">
          <input
            value={searchInput}
            onChange={handleSearch}
            onKeyDown={handleEnterSearch}
            className="round-search-input"
            style={{ fontSize: 12 }}
            placeholder="Buscar por código o actividad"
          />
          {searchInput === '' && (
            <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
          )}
          {searchInput && (
            <div
              title="Limpiar texto"
              onClick={() => {
                setSearchInput('')
                onSearchQuery('')
              }}
              className="round-search-input-x"
            >
              X
            </div>
          )}
        </div>
      </div>

      <div className="mb-20">
        {(filterQuery || searchInput) && (
          <a onClick={resetFilters} className="reset-filter cursor-pointer mb-20">
            Restablecer filtros aplicados
          </a>
        )}
      </div>

      <Table hover responsive>
        <thead className="table-header-gray">
          <tr>
            {headers.map((h, i) => (
              <th key={i}>
                <span className="th-header-text">{h}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {!isTableDataFetching &&
            tableData?.map((activity, i) => (
              <tr key={i}>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {dayjs(activity.created_date).format('DD/MM/YYYY, h:mm:ss A')}
                </td>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {activity.salesman_db_ref} - {activity.salesman_name}
                </td>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {formatActivityName(activity.activity_name)}
                </td>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {activity.activity_uid}
                </td>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {activity.total ? formatMoney(activity.total) : '—'}
                </td>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {activity.effective_location ? 'SI' : 'NO'}
                </td>
                <td>
                  <button
                    onClick={(e) => {
                      handleShow(activity, e)
                    }}
                    className="report-maps-activity-button"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faMapMarkedAlt} />
                  </button>
                </td>
                <td
                  onClick={(e) => {
                    handleTransactionClick(e, activity)
                  }}
                >
                  {handleStatus(activity.status)}
                </td>
              </tr>
            ))}
          {isTableDataFetching && <LoadingTable text="Cargando..." headersCount={headers.length} />}
          {!isTableDataFetching && tableData?.length == 0 && (
            <tr>
              <td colSpan={headers.length}>No hay resultados disponibles.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {!isTableDataFetching && tableData?.length > 0 && totalPages > 1 && (
        <nav className="index-table" onClick={handlePaginationClick}>
          <Pagination current_page={currentPage} total_pages={totalPages} />
        </nav>
      )}
      <MapModalProvider>
        <Modal
          show={show}
          dialogClassName="modal-90w"
          onHide={handleClose}
          aria-labelledby="example-modal-sizes-title-lg"
        >
          <Modal.Header closeButton className="no-border-bottom">
            <Modal.Title id="example-modal-sizes-title-lg">
              <b className="ft-16">Reporte: {dataSelected.salesman_name}</b>
            </Modal.Title>
          </Modal.Header>

          <Modal.Header className="d-flex flex-column align-items-start">
            <b className="mb-10">Detalles de captura de actividad</b>
            <p className="text-title-modal-map">Cliente: {dataSelected.client_name}</p>
            <p className="text-title-modal-map">
              Fecha y hora: {dayjs(dataSelected.created_date).format('L')} -{' '}
              {dayjs(dataSelected.created_date).format('LT')}
            </p>
            <p className="text-title-modal-map">Actividad: {dataSelected.activity_name}</p>
          </Modal.Header>
          <Modal.Body>
            <div id="map-canvas">
              <ShowMapModalContent
                longitude={longitude}
                latitude={latitude}
                clientLatitude={dataSelected.client_latitude}
                clientLongitude={dataSelected.client_longitude}
                salesmanVisitData={dataSelected}
                hasAllowedLocation={dataSelected.has_allowed_location}
                errorCoordinatesCode={dataSelected.error_coordinates_code}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleClose} className="custom-close-button">
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      </MapModalProvider>
    </>
  )
}
